import { Center, HStack, RadioGroup, Stack } from "@chakra-ui/react"
import SubmitButton from "@pathwright/ui/src/components/button/SubmitButton"
import CardBlock from "@pathwright/ui/src/components/card/CardBlock"
import { useRefetchCoverPageOffers } from "@pathwright/features/src/modules/cover-pages/utils"
import CurrencyFormatter from "@pathwright/features/src/modules/currency/CurrencyFormatter"
import ProductCheckboxes, {
  useProducts
} from "@pathwright/features/src/modules/stripe/product/ProductCheckboxes"
import SchoolSubscriptionPlansCheckboxes, {
  useSchoolSubscriptionPlans
} from "@pathwright/features/src/modules/subscription/manage/SchoolSubscriptionPlansCheckboxes"
import { ConnectForm, TextInput, validate } from "lib/core/components/forms"
import FieldWrapper from "lib/core/components/forms/form-utils/FieldWrapper"

export const COST_OPTIONS = {
  SUBSCRIPTION_ONLY: 1,
  NO_CHARGE: 2,
  CHARGE: 3
}

const hasCost = (fields) => fields.nonsubpricing.value === COST_OPTIONS.CHARGE

let fieldsData = (props) => ({
  cost_per_seat: {
    type: "number",
    step: "any",
    minWidth: 30,
    labelWidth: 110,
    autoSize: true,
    inline: true,
    optional: true,
    validations: [
      validate.validateIf(hasCost, validate.isNumeric),
      validate.validateIf(
        hasCost,
        validate.isGreaterThanOrEqualTo(
          0.5,
          (n) => parseFloat(n).toFixed(2) /* parser */
        )
      ),
      validate.validateIf(hasCost, (values, value) => {
        const isValid = props.canAcceptPayments || value <= 0
        if (!isValid) {
          return "You must set up a Stripe account first."
        }
      })
    ]
  },
  nonsubpricing: {
    label: "Pricing"
  },
  subpricing: {
    label: "Pricing for subscribers"
  },
  products: {
    label: "Products",
    includeInactive: true
  }
})

const uniformLegacyStyles = {
  fontWeight: "400",
  fontSize: "1em",
  py: 2
}

const CommerceForm = function ({
  fieldsData,
  isValid,
  handleSubmit: onSubmit,
  group,
  submitFailure,
  submitSuccess,
  submitting
}) {
  const refetchCoverPageOffers = useRefetchCoverPageOffers()

  async function handleSubmit(form, resetForm) {
    await onSubmit(form, resetForm)
    await refetchCoverPageOffers()
  }

  const { schoolSubscriptionPlans } = useSchoolSubscriptionPlans()
  const { products } = useProducts({ includeInactive: true })
  const hasSubscriptionPlans =
    schoolSubscriptionPlans && schoolSubscriptionPlans.length > 0
  const hasProducts = products.length > 0

  // run subpricing validations whenever nonsubpricing changes
  const handleChange = (value) => {
    const { nonsubpricing, subpricing, cost_per_seat } = fieldsData
    nonsubpricing.onChange(value)
    cost_per_seat.onChange(cost_per_seat.value)
    subpricing.onChange(subpricing.value)
  }

  let isSubOnlyValid = true
  // require at least 1 sub plan or 1 product to be selected if making unavailable to non-subscribers
  if (
    fieldsData.nonsubpricing.value === COST_OPTIONS.SUBSCRIPTION_ONLY &&
    (!hasSubscriptionPlans ||
      (hasSubscriptionPlans && !fieldsData.subpricing.value.length)) &&
    (!hasProducts || (hasProducts && !fieldsData.products.value.length))
  ) {
    isSubOnlyValid = false
  }

  // Our validation isn't behaving as expected, so we're adding this to ensure the form is disabled if the cost per seat is missing.
  const missingCostPerSeat =
    hasCost(fieldsData) && !fieldsData.cost_per_seat.value

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <FieldWrapper
          label={fieldsData.nonsubpricing.label}
          labelWidth={fieldsData.nonsubpricing.labelWidth}
        >
          <RadioGroup.Root
            value={fieldsData.nonsubpricing.value}
            onValueChange={(e) => handleChange(e.value)}
            colorPalette="blue"
            display="flex"
            flexDirection="column"
            gap={0}
          >
            {(hasSubscriptionPlans || hasProducts) && (
              <RadioGroup.Item
                value={COST_OPTIONS.SUBSCRIPTION_ONLY}
                {...uniformLegacyStyles}
              >
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>
                  Not available for non-subscribers.
                </RadioGroup.ItemText>
              </RadioGroup.Item>
            )}
            <RadioGroup.Item
              value={COST_OPTIONS.NO_CHARGE}
              {...uniformLegacyStyles}
            >
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemText>Free</RadioGroup.ItemText>
            </RadioGroup.Item>

            <HStack gap={1}>
              <RadioGroup.Item
                value={COST_OPTIONS.CHARGE}
                {...uniformLegacyStyles}
              >
                <RadioGroup.ItemHiddenInput />
                <RadioGroup.ItemIndicator />
                <RadioGroup.ItemText>Charge</RadioGroup.ItemText>
              </RadioGroup.Item>
              <CurrencyFormatter>
                <TextInput
                  {...fieldsData.cost_per_seat}
                  onFocus={() =>
                    fieldsData.nonsubpricing.onChange(COST_OPTIONS.CHARGE)
                  }
                />
              </CurrencyFormatter>
            </HStack>
          </RadioGroup.Root>
        </FieldWrapper>

        {hasSubscriptionPlans && (
          <CardBlock>
            <SchoolSubscriptionPlansCheckboxes {...fieldsData.subpricing} />
          </CardBlock>
        )}
        {hasProducts && (
          <CardBlock>
            <ProductCheckboxes
              {...fieldsData.products}
              selectedProductIds={fieldsData.products.value}
            />
          </CardBlock>
        )}

        <Center>
          <SubmitButton
            submitting={submitting}
            submitSuccess={submitSuccess}
            submitFailure={submitFailure}
            disabled={!isValid || !isSubOnlyValid || missingCostPerSeat}
            styleType={"primary"}
          >
            Save
          </SubmitButton>
        </Center>
      </Stack>
    </form>
  )
}

CommerceForm.displayName = "CommerceForm"

export default ConnectForm(CommerceForm, fieldsData)
